import { PayloadAction, createSlice, isPending, isRejectedWithValue } from '@reduxjs/toolkit';

import { getAdministrationHistoryListThunk } from '../thunks/analytics';
import { AnalyticsState } from 'types/analytics';
import { INITIAL_LIST_PARAMS } from 'config';
import { DateTypes } from 'types/client';

const initialState: AnalyticsState = {
  isLoading: false,
  analyticsAppList: {},
  analyticsAppListParams: INITIAL_LIST_PARAMS,
  analyticsAppFiltersListParams: INITIAL_LIST_PARAMS,
  analyticsAppFiltersErrors: {
    [DateTypes.CREATED_AT_START]: '',
    [DateTypes.CREATED_AT_END]: '',
    [DateTypes.PAID_AT_START]: '',
    [DateTypes.PAID_AT_END]: '',
  },
  analyticsPathList: {},
  analyticsPathListParams: INITIAL_LIST_PARAMS,
  analyticsPathFiltersListParams: INITIAL_LIST_PARAMS,
  analyticsPathFiltersErrors: {
    [DateTypes.PAID_AT_START]: '',
    [DateTypes.PAID_AT_END]: '',
  },
  analyticsServiceList: {},
  analyticsServiceListParams: INITIAL_LIST_PARAMS,
  analyticsServiceFiltersListParams: INITIAL_LIST_PARAMS,
  analyticsServiceFiltersErrors: {
    [DateTypes.CREATED_AT_START]: '',
    [DateTypes.CREATED_AT_END]: '',
    [DateTypes.PAID_AT_START]: '',
    [DateTypes.PAID_AT_END]: '',
  },
  administrationHistoryList: {},
  serviceClientList: {},
  serviceClientListParams: INITIAL_LIST_PARAMS,
  serviceFiltersListParams: INITIAL_LIST_PARAMS,
  serviceFiltersErrors: {
    [DateTypes.CREATED_AT_START]: '',
    [DateTypes.CREATED_AT_END]: '',
    [DateTypes.PAID_AT_START]: '',
    [DateTypes.PAID_AT_END]: '',
    product_ids: '',
  },
  appClientList: {},
  appClientListParams: INITIAL_LIST_PARAMS,
  appFiltersListParams: INITIAL_LIST_PARAMS,
  appFiltersErrors: {
    [DateTypes.CREATED_AT_START]: '',
    [DateTypes.CREATED_AT_END]: '',
    [DateTypes.PAID_AT_START]: '',
    [DateTypes.PAID_AT_END]: '',
    product_ids: '',
  },
};

const slice = createSlice({
  name: 'analytics',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },

    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },

    resetError(state) {
      state.error = undefined;
    },

    getAnalyticsAppList(state, action) {
      state.isLoading = false;
      state.analyticsAppList = action.payload;
    },

    setAnalyticsAppParams(state, action) {
      state.isLoading = false;
      state.analyticsAppListParams = action.payload;
    },

    setAnalyticsAppFiltersListParams(state, action) {
      state.isLoading = false;
      state.analyticsAppFiltersListParams = action.payload;
    },

    setAnalyticsAppFiltersErrors(state, action) {
      state.isLoading = false;
      state.analyticsAppFiltersErrors = action.payload;
    },

    setAnalyticsServiceParams(state, action) {
      state.isLoading = false;
      state.analyticsServiceListParams = action.payload;
    },

    setAnalyticsServiceFiltersListParams(state, action) {
      state.isLoading = false;
      state.analyticsServiceFiltersListParams = action.payload;
    },

    setAnalyticsServiceFiltersErrors(state, action) {
      state.isLoading = false;
      state.analyticsServiceFiltersErrors = action.payload;
    },

    setAnalyticsPathParams(state, action) {
      state.isLoading = false;
      state.analyticsPathListParams = action.payload;
    },

    setAnalyticsPathFiltersListParams(state, action) {
      state.isLoading = false;
      state.analyticsPathFiltersListParams = action.payload;
    },

    setAnalyticsPathFiltersErrors(state, action) {
      state.isLoading = false;
      state.analyticsPathFiltersErrors = action.payload;
    },

    getAnalyticsServiceList(state, action) {
      state.isLoading = false;
      state.analyticsServiceList = action.payload;
    },

    getAnalyticsPathList(state, action) {
      state.isLoading = false;
      state.analyticsPathList = action.payload;
    },

    getServiceAnalyticsClientListSuccess(state, action) {
      state.isLoading = false;
      state.serviceClientList = action.payload;
    },

    setServiceAnalyticsClientListParams(state, action) {
      state.serviceClientListParams = action.payload;
    },

    setServiceAnalyticsFiltersListParams(state, action) {
      state.isLoading = false;
      state.serviceFiltersListParams = action.payload;
    },

    setServiceAnalyticsFiltersErrors(state, action) {
      state.isLoading = false;
      state.serviceFiltersErrors = action.payload;
    },

    getAppAnalyticsClientListSuccess(state, action) {
      state.isLoading = false;
      state.appClientList = action.payload;
    },

    setAppAnalyticsClientListParams(state, action) {
      state.appClientListParams = action.payload;
    },

    setAppAnalyticsFiltersListParams(state, action) {
      state.isLoading = false;
      state.appFiltersListParams = action.payload;
    },

    setAppAnalyticsFiltersErrors(state, action) {
      state.isLoading = false;
      state.appFiltersErrors = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        getAdministrationHistoryListThunk.fulfilled,
        (state, { payload }: PayloadAction<any>) => {
          state.isLoading = false;
          state.administrationHistoryList = payload;
        }
      )
      //getAdministrationHistoryListThunk
      .addMatcher(isPending(getAdministrationHistoryListThunk), (state) => {
        // state.administrationHistoryList.error = '';
      })
      .addMatcher(isRejectedWithValue(getAdministrationHistoryListThunk), (state, { payload }) => {
        // state.administrationHistoryList.error = payload;
      });
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  startLoading,
  hasError,
  resetError,
  getAnalyticsAppList,
  setAnalyticsAppFiltersListParams,
  setAnalyticsAppFiltersErrors,
  getAnalyticsServiceList,
  getAnalyticsPathList,
  setAnalyticsPathParams,
  setAnalyticsPathFiltersListParams,
  setAnalyticsPathFiltersErrors,
  setAnalyticsServiceParams,
  setAnalyticsServiceFiltersListParams,
  setAnalyticsServiceFiltersErrors,
  setAnalyticsAppParams,
  getServiceAnalyticsClientListSuccess,
  setServiceAnalyticsClientListParams,
  setServiceAnalyticsFiltersListParams,
  setServiceAnalyticsFiltersErrors,
  getAppAnalyticsClientListSuccess,
  setAppAnalyticsClientListParams,
  setAppAnalyticsFiltersListParams,
  setAppAnalyticsFiltersErrors,
} = slice.actions;
